import React, { Suspense, lazy, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "react-toastify/dist/ReactToastify.css";

import PrivateRoute from "./routes/Private-Routes";
import PublicRoute from "./routes/Public-Routes";
import PermissionGuard from "./routes/PermissionGuard";
import { addPermissionModules, addAccessData } from "./redux/userSlice";
import { addCmsData } from "./redux/cmsSlice";
import { addScreenSize } from "./redux/screenSize.js";
import axiosInstance from "./services/axios";
import {
  extractModuleList,
  getModulesApiPath,
  normalizePermissionModule,
} from "./permissions/modules";
import {
  EMPTY_ACCESS,
  extractAccessData,
  getAccessDataApiPath,
  normalizeAccessData,
} from "./permissions/accessData";

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type = process.env.REACT_APP_API_TYPE;

// Public entry pages are also lazy-loaded so route chunks are only requested when needed.
const LoginMainPage = lazy(() => import("./pages/LoginNew/index.jsx"));

// Primary private route pages are lazy-loaded to keep the initial bundle smaller and improve
// the app's first-load performance.
const RegistrationManagementNewPage = lazy(
  () => import("./pages/RegistrationManagementNew/index.jsx")
);
const ClientManagementNewPage = lazy(
  () => import("./pages/ClientManagementNew/index.jsx")
);
const BondManagementNewPage = lazy(
  () => import("./pages/BondManagementNew/index.jsx")
);
const ContentManagementNewPage = lazy(
  () => import("./pages/ContentManagementNew/index.jsx")
);
const ControlCenterNewPage = lazy(
  () => import("./pages/ControlCenterNew/index.jsx")
);
const UserManagementNewPage = lazy(
  () => import("./pages/UserManagementNew/index.jsx")
);
const DashboardNewPage = lazy(() => import("./pages/DashboardNew/index.jsx"));
const OrderManagementPage = lazy(
  () => import("./pages/OrderManagement/index.jsx")
);
const ReportMain = lazy(() => import("./pages/Report/ReportMain"));
const InvestorReportEdit = lazy(
  () => import("./pages/Report/InvestorReportEdit")
);
const UserManagementMain = lazy(
  () => import("./pages/UserManagement/UserManagementMain")
);
const UserCreation = lazy(() => import("./pages/UserManagement/UserCreation"));
const AddUserManagement = lazy(
  () => import("./pages/UserManagement/AddUserManagement")
);
const EditUserManagement = lazy(
  () => import("./pages/UserManagement/edituserManagement")
);
const InvestorManagementMainPage = lazy(
  () => import("./pages/InvestorManagement")
);
const InvestorDetails = lazy(
  () => import("./pages/InvestorManagement/InvestorDetails")
);
const InvestorManagementPartner = lazy(
  () => import("./pages/InvestorManagement/InvestorManagmentParnter")
);
const InvestorManagementParnterDetails = lazy(
  () => import("./pages/InvestorManagement/InvestorManagmentPartnerDetails")
);
const BondManagementMainPage = lazy(
  () => import("./pages/BondManagement/index.jsx")
);
const RegistrationPartnerDetails = lazy(
  () => import("./pages/RegistartionManagement/RegistrationPartnerDetails")
);
const RelationshipManagerMain = lazy(
  () => import("./pages/RelationshipManager/RelationshipManagerMain")
);
const SellRequestMain = lazy(() => import("./pages/SellRequest/SellRequest"));
const InviteWealthPartner = lazy(() => import("./pages/InviteWP"));
const ManageClientMainPage = lazy(() => import("./pages/ManageClient"));
const ManageResourceMainPage = lazy(() => import("./pages/ManageResounces"));
const ManageResouncesNewMainPage = lazy(
  () => import("./pages/ManageResounces/mainPage.jsx")
);
const LearningVideoPlaylistPage = lazy(
  () => import("./pages/ManageResounces/Training/LearningVideoAsset")
);
const LearningVideoViewPlaylist = lazy(
  () =>
    import(
      "./pages/ManageResounces/Training/LearningVideoAsset/LearningVideoViewPlaylist"
    )
);
const HandbookAssetsListPage = lazy(
  () =>
    import("./pages/ManageResounces/Training/Handbook/HandbookAssetList")
);

// Legacy standalone routes are preserved as-is, but still lazy-loaded for better chunking.
const BondDirectory = lazy(
  () => import("./pages/BondDirectory/BondDirectory.jsx")
);
const PushNotificationPage = lazy(
  () => import("./pages/PushNotification/index.jsx")
);
const UsersManagementPage = lazy(
  () => import("./pages/UsersManagement/index.jsx")
);
const AccountClosureRequestsPage = lazy(
  () => import("./pages/AccountClosureRequests/index.jsx")
);
const NoAccessPage = lazy(() => import("./pages/NoAccess/index.jsx"));

// Lightweight route fallback shown while lazy route chunks are loading.
function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-[#6B7A90]">
      Loading...
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const { authToken, isAuthenticated } = useSelector((state) => state.login);

  // Normalize auth into one boolean so route guards behave consistently even when Redux
  // restores persisted string values from localStorage.
  const isUserAuthenticated = Boolean(
    authToken || isAuthenticated === true || isAuthenticated === "true"
  );

  // Fetch CMS bootstrap content once an authenticated session is available.
  const getCmsData = useCallback(async () => {
    const endpoint = `${base_url}/${api_type}/cms/homeContent/get`;
    const token = authToken || localStorage.getItem("token");

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(addCmsData(response?.data?.data));
    } catch (err) {
      console.error("Failed to fetch bond details:", err);
    }
  }, [authToken, dispatch]);

  const getPermissionModules = useCallback(async () => {
    try {
      const response = await axiosInstance.get(getModulesApiPath());
      const modules = extractModuleList(response?.data).map((item, index) =>
        normalizePermissionModule(item, index)
      );
      dispatch(addPermissionModules(modules));
    } catch (error) {
      console.error("Failed to fetch permission modules:", error);
      dispatch(addPermissionModules([]));
    }
  }, [dispatch]);

  const getAccessData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(getAccessDataApiPath());
      dispatch(
        addAccessData(normalizeAccessData(extractAccessData(response?.data)))
      );
    } catch (error) {
      console.error("Failed to fetch access data:", error);
      dispatch(addAccessData(EMPTY_ACCESS));
    }
  }, [dispatch]);

  // Keep the shared screen-size slice in sync with the viewport while throttling resize work
  // through requestAnimationFrame to avoid excessive dispatching.
  useEffect(() => {
    let frameId = 0;

    const updateScreenSize = () => {
      dispatch(addScreenSize(window.innerWidth));
    };

    const handleResize = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateScreenSize);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch]);

  // Bootstrap permission and CMS data in parallel after authentication so the dashboard
  // and navigation can render with the required shared data.
  useEffect(() => {
    if (!isUserAuthenticated) return;

    const bootstrapAuthenticatedApp = async () => {
      await Promise.all([
        getCmsData(),
        getPermissionModules(),
        getAccessData(),
      ]);
    };

    bootstrapAuthenticatedApp();
  }, [getAccessData, getCmsData, getPermissionModules, isUserAuthenticated]);

  // Grouping routes into config arrays keeps the routing tree easier to maintain and makes
  // it straightforward to reuse the same auth wrappers.
  const publicRoutes = [
    { path: "/", element: <LoginMainPage /> },
    { path: "/login", element: <LoginMainPage /> },
    { path: "/login/:url", element: <LoginMainPage /> },
    ];

  const privateRoutes = [
    { path: "/registration/:url", element: <RegistrationManagementNewPage /> },
    {
      path: "/registration/investor/detail/:id",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/investor/deletion-log",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/distributor/detail/:id",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/distributor/deletion-log",
      element: <RegistrationManagementNewPage />,
    },
    { path: "/client-management/:url", element: <ClientManagementNewPage /> },
    { path: "/bond-management/:url", element: <BondManagementNewPage /> },
    {
      path: "/content-management/:url",
      element: <ContentManagementNewPage />,
    },
    {
      path: "/content-management/manage-resource/:url",
      element: <ContentManagementNewPage />,
    },
    { path: "/control-center/:url", element: <ControlCenterNewPage /> },
    { path: "/user-management", element: <UserManagementNewPage /> },
    {
      path: "/registration/wealth-partner/details",
      element: <RegistrationPartnerDetails />,
    },
    { path: "/no-access", element: <NoAccessPage /> },
    { path: "/dashboard", element: <DashboardNewPage /> },
    { path: "/order-management", element: <OrderManagementPage /> },
    {
      path: "/bond-management/bond-catalogue",
      element: <BondManagementMainPage />,
    },
    { path: "/bond/edit", element: <BondManagementMainPage /> },
    { path: "/bond/add", element: <BondManagementMainPage /> },
    { path: "/report", element: <ReportMain /> },
    { path: "/report/edit", element: <InvestorReportEdit /> },
    { path: "/user", element: <UserManagementMain /> },
    { path: "/user/creation", element: <UserCreation /> },
    { path: "/user/new-user", element: <AddUserManagement /> },
    { path: "/user/edit-user/:userId", element: <EditUserManagement /> },
    {
      path: "/investment-management/investor",
      element: <InvestorManagementMainPage />,
    },
    {
      path: "/investment-management/investor/details",
      element: <InvestorDetails />,
    },
    {
      path: "/investment-management/wealth-partner",
      element: <InvestorManagementPartner />,
    },
    {
      path: "/investment-management/wealth-partner/details",
      element: <InvestorManagementParnterDetails />,
    },
    { path: "/relationship-manager", element: <RelationshipManagerMain /> },
    { path: "/sell-request", element: <SellRequestMain /> },
    { path: "/invite-partner", element: <InviteWealthPartner /> },
    { path: "/manage-client/:name", element: <ManageClientMainPage /> },
    { path: "/manage-resource", element: <ManageResourceMainPage /> },
    { path: "/manage-resource/:id", element: <ManageResouncesNewMainPage /> },
    {
      path: "/manage-resource/training/learning/:id",
      element: <LearningVideoPlaylistPage />,
    },
    {
      path: "/manage-resource/training/learning/playlist/:id",
      element: <LearningVideoViewPlaylist />,
    },
    {
      path: "/push-notifications/broadcast",
      element: <PushNotificationPage />,
    },
    {
      path: "/push-notifications/multicast",
      element: <PushNotificationPage />,
    },
    {
      path: "/users-management/groups",
      element: <UsersManagementPage />,
    },
    {
      path: "/users-management/admins",
      element: <UsersManagementPage />,
    },
    {
      path: "/account-closure-requests",
      element: <AccountClosureRequestsPage />,
    },
    {
      path: "/manage-resource/training/handbook/:id",
      element: <HandbookAssetsListPage />,
    },
  ];

  // These routes previously bypassed the auth wrapper, so they are preserved that way here
  // to avoid changing existing route behavior unexpectedly.
  const standaloneRoutes = [
    { path: "/bond-directory", element: <BondDirectory /> }
  ];

  // Wrapper helpers keep the route declarations compact and consistent.
  const renderPublicRoute = (element) => (
    <PublicRoute isAuthenticated={isUserAuthenticated}>{element}</PublicRoute>
  );

  const renderPrivateRoute = (element) => (
    <PrivateRoute isAuthenticated={isUserAuthenticated}>
      <PermissionGuard>{element}</PermissionGuard>
    </PrivateRoute>
  );

  return (
    <>
      <ToastContainer />
      <Router>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            {publicRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={renderPublicRoute(element)}
              />
            ))}

            {privateRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={renderPrivateRoute(element)}
              />
            ))}

            {standaloneRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </Suspense>
      </Router>
    </>
  );
}

export default App;
